import type { IProjectData, IPlanData } from '@datatype/gridgio/api-type';

export type TAnnotationDialogMode = 'property' | 'memo' | 'memo-list' | 'photo' | 'delete';
export type TAreaType = 'drawing' | 'key-plan';

export enum EWorkPhase {
  PROJECT_SETUP = 'project-setup',
  DRAWING_SETUP = 'drawing-setup',
  ANNOTATION = 'annotation',
}

export interface IPoint {
  x: number;
  y: number;
}

export interface IArea {
  point: [IPoint, IPoint, IPoint, IPoint];
}

export interface IStroke {
  point: IPoint[];
}

export interface IAnnotationProperty {
  type: 'width' | 'length';
  stroke: IStroke[];
  value?: number;
  unit: 'mm';
  canvas: IMemoCanvas;
}

export interface IMemoCanvas {
  /** 입력 영역의 CSS 픽셀 크기. stroke는 같은 좌표계를 사용한다. */
  width: number;
  height: number;
}

export interface IAnnotationMemo {
  id: string;
  canvas: IMemoCanvas;
  stroke: IStroke[];
  updatedAt?: string;
}

export interface IAnnotation {
  id: string;
  issueId: string;
  mark: {
    stroke: IStroke[];
  };
  memo: IAnnotationMemo[];
  property?: IAnnotationProperty[];
}

export interface IDrawing {
  file: File;
  url: string;
  name: string;
  type: 'pdf' | 'image';
}

export interface IWorkspaceView {
  zoom: number;
  x: number;
  y: number;
}

export interface IGridgioWork {
  drawing: IDrawing;
  rotation: number;
  workArea: IArea | null;
  keyPlanArea: IArea | null;
  annotation: Record<string, IAnnotation[]>;
  totalPage: number;
}

// ############### dialog modal ###############
export type TDialogModal =
  | IPlainTextModalData
  | IFileSavedModalData
  | IFeaturePlannedModalData
  | ISourceCreateModalData
  | IPlanCreateModalData
  | IPlanRemoveModalData
  | IPlanEditModalData
  | IProjectEditModalData
  | IProjectRemoveModalData
  | IProjectBookmarkRemoveModalData
  | IProjectCreateModalData
  | IStartWorkModalData
  | IWorkEditModalData
  | IWorkRemoveModalData
  | ICreateWorkModalData
  | ISourcePreviewModalData
  | IPlanPreviewModalData
  | IMapPreviewModalData
  | IAnnotationModalData;

export interface IFileSavedModalData {
  type: 'file-saved';
  data: { filename: string };
}

export interface IPlainTextModalData {
  type: 'plain-text';
  data: string;
}

export interface IFeaturePlannedModalData {
  type: 'feature-planned';
  data?: { returnTo?: IStartWorkModalData };
}

export interface ISourceCreateModalData {
  type: 'source-create';
}

export interface IPlanCreateModalData {
  type: 'plan-create';
  data: { projectKey: string };
}

export interface IPlanEditModalData {
  type: 'plan-edit';
  data: { projectKey: string; planKey: string; name: string; description?: string };
}

export interface IPlanRemoveModalData {
  type: 'plan-remove';
  data: { projectKey: string; planKey: string };
}

export interface IProjectEditModalData {
  type: 'project-edit';
  data: IProjectData;
}

export interface IProjectRemoveModalData {
  type: 'project-remove';
  data: { projectKey: string };
}

export interface IProjectBookmarkRemoveModalData {
  type: 'project-bookmark-remove';
  data: { projectKey: string };
}

export const isProjectBookmarkRemoveModalDataType = (data: TDialogModal | null): data is IProjectBookmarkRemoveModalData =>
  data !== null && data.type === 'project-bookmark-remove';

export const isProjectEditModalDataType = (data: TDialogModal | null): data is IProjectEditModalData =>
  data !== null && data.type === 'project-edit';

export const isProjectRemoveModalDataType = (data: TDialogModal | null): data is IProjectRemoveModalData =>
  data !== null && data.type === 'project-remove';

export interface IProjectCreateModalData {
  type: 'project-create';
}

export interface IStartWorkModalData {
  type: 'start-work';
  data: { projectKey: string; initialStep?: 'method' | 'plan' };
}

export interface IWorkEditModalData {
  type: 'work-edit';
  data: { workKey: string; name: string; description?: string };
}

export interface IWorkRemoveModalData {
  type: 'work-remove';
  data: { projectKey: string; workKey: string };
}

export const isWorkEditModalDataType = (data: TDialogModal | null): data is IWorkEditModalData =>
  data !== null && data.type === 'work-edit';

export const isWorkRemoveModalDataType = (data: TDialogModal | null): data is IWorkRemoveModalData =>
  data !== null && data.type === 'work-remove';

export interface ICreateWorkModalData {
  type: 'create-work';
  data: { projectKey: string; plan: IPlanData };
}

export interface ISourcePreviewModalData {
  type: 'source-preview';
  data: { url: string; type: 'pdf' | 'image' };
}

export interface IPlanPreviewModalData {
  type: 'plan-preview';
  data: { url: string; type: 'pdf' | 'image' };
}

export interface IAnnotationModalData {
  type: 'annotation';
  data: {
    workKey: string;
    sheetId: string;
    annotationId: string;
    mode: Exclude<TAnnotationDialogMode, 'photo'>;
    memoId?: string | null;
  };
}

export interface IMapPreviewModalData {
    type: 'map-preview';
    data: { latitude: number; longitude: number };
}

export const isFeaturePlannedModalDataType = (data: TDialogModal | null): data is IFeaturePlannedModalData => {
  return data !== null && data.type === 'feature-planned';
};

export const isSourceCreateModalDataType = (data: TDialogModal | null): data is ISourceCreateModalData => {
  return data !== null && data.type === 'source-create';
};

export const isProjectCreateModalDataType = (data: TDialogModal | null): data is IProjectCreateModalData => {
  return data !== null && data.type === 'project-create';
};

export const isStartWorkModalDataType = (data: TDialogModal | null): data is IStartWorkModalData => {
  return data !== null && data.type === 'start-work';
};

export const isCreateWorkModalDataType = (data: TDialogModal | null): data is ICreateWorkModalData => {
  return data !== null && data.type === 'create-work';
};

export const isSourcePreviewModalDataType = (data: TDialogModal | null): data is ISourcePreviewModalData => {
  return data !== null && data.type === 'source-preview';
};

export const isAnnotationModalDataType = (data: TDialogModal | null): data is IAnnotationModalData => {
  return data !== null && data.type === 'annotation';
};

export const isPlainTextModalDataType = (data: TDialogModal | null): data is IPlainTextModalData => {
  return data !== null && data.type === 'plain-text';
};

export const isFileSavedModalDataType = (data: TDialogModal | null): data is IFileSavedModalData => {
  return data !== null && data.type === 'file-saved';
};

export const isPlanRemoveModalDataType = (data: TDialogModal | null): data is IPlanRemoveModalData => {
  return data !== null && data.type === 'plan-remove';
};

export const isPlanEditModalDataType = (data: TDialogModal | null): data is IPlanEditModalData => {
  return data !== null && data.type === 'plan-edit';
};

export const isPlanCreateModalDataType = (data: TDialogModal | null): data is IPlanCreateModalData => {
  return data !== null && data.type === 'plan-create';
};

export const isPlanPreviewModalDataType = (data: TDialogModal | null): data is IPlanPreviewModalData => {
  return data !== null && data.type === 'plan-preview';
};

export const isMapPreviewModalDataType = (data: TDialogModal | null): data is IMapPreviewModalData =>
  data !== null && data.type === 'map-preview';
